import { FC, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { IDictionaryItem } from 'api/types';
import { StudentSectionSettingTypeCodes, studyNavigation } from 'const';
import { useAppSelector, useBrightTheme, useDataLoad, useWidgetVisibility } from 'hooks';
import { IconBarChart, IconGridFilled, IconPieChartFilled } from 'icons';
import { placeholderCharacter, widgetIllustrations } from 'images';
import NoDataPlug from 'portfolio3/components/common/NoDataPlug';
import { WidgetContainer as Widget } from 'portfolio3/components/common/WidgetContainer';
import { WidgetContainerOptions, WidgetContainerTitle } from 'portfolio3/components/widgetContainerRecipes';
import { emitYMEvent } from 'portfolio3/features/yandexMetrika';
import { commonTheme } from 'portfolio3/styles/theme';
import { Switcher, SwitcherButton } from 'portfolio3/ui-kit';
import { getCurrentLearningYear, getDataLoadRequests } from 'utils';

import PerformanceFilters from '../components/PerformanceFilters';
import {
  finalMarkDiagramLearningYearsSelector,
  finalMarkUnfilteredYearsSelector,
  learningYearAveragePerformanceSelector,
  learningYearFinalPerformanceSelector,
} from '../model/selectors';
import { IMappedAverageGrade, IMappedFinalGrades, MarkType, VisibilityType } from '../types';
import { sortLearningYears } from '../utils';
import PerformanceContainer from './PerformanceContainer';
import { IYearPerformance } from './PerformanceContainer.types';

const sectionTypeCode = StudentSectionSettingTypeCodes.study;
const subsectionTypeCode = StudentSectionSettingTypeCodes.studyPerformance;

const getCurrentPerfomance = ({
  finalPerformance,
  averagePerformance,
  markType,
  currentLearningYear,
}: {
  finalPerformance: IMappedFinalGrades[];
  averagePerformance: IMappedAverageGrade;
  markType: MarkType;
  currentLearningYear?: string;
}): IYearPerformance => {
  if (markType === MarkType.FINAL) {
    const currentYearFinalGrade = finalPerformance.find((performance) => performance.year === currentLearningYear);

    return {
      subjectsPerformance: currentYearFinalGrade?.subjects ?? [],
    };
  }

  return averagePerformance;
};

const PerformanceWidgetContainer: FC = () => {
  const finalPerformance = useAppSelector(learningYearFinalPerformanceSelector);
  const averagePerformance = useAppSelector(learningYearAveragePerformanceSelector);
  const finalMarkDiagramLearningYears = useAppSelector(finalMarkDiagramLearningYearsSelector);
  const finalMarkUnfilteredYears = useAppSelector(finalMarkUnfilteredYearsSelector);

  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));

  const isBrightTheme = useBrightTheme();
  const { isSettingsMode, isWidgetVisible, toggleElement, overlayElement } = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
  });

  useDataLoad({ shouldLoad: isWidgetVisible, requests: getDataLoadRequests().performanceEntryMethods });

  const [currentVisibilityType, setCurrentVisibilityType] = useState<VisibilityType>(VisibilityType.DIAGRAM);
  const initialFilterYear = 1;
  const [filterYear, setFilterYear] = useState(initialFilterYear);
  const [markType, setMarkType] = useState<MarkType>(MarkType.AVERAGE);

  const finalMarkLearningYears = useMemo(() => {
    if (markType === MarkType.AVERAGE || finalPerformance.content.length === 0) {
      return [getCurrentLearningYear()];
    }
    let learningYears: string[];

    if (currentVisibilityType === VisibilityType.DIAGRAM) {
      learningYears = finalMarkDiagramLearningYears;
    } else {
      learningYears = finalMarkUnfilteredYears;
    }

    return learningYears.length === 0 ? [getCurrentLearningYear()] : learningYears;
  }, [currentVisibilityType, finalPerformance, markType, finalMarkDiagramLearningYears, finalMarkUnfilteredYears]);

  const sortedLearningYears = sortLearningYears(finalMarkLearningYears, 'desc');
  const learningYearOptions: IDictionaryItem[] = sortedLearningYears.map((year, index) => ({
    code: index + 1,
    value: year,
  }));

  const mappedLearningYearOptions = learningYearOptions.map((option) => {
    return {
      code: option.code,
      value: option.value + ' уч. год',
    };
  });

  const currentLearningYear = learningYearOptions.find((year) => year.code === filterYear)?.value;

  const currentPerformance = getCurrentPerfomance({
    finalPerformance: finalPerformance.content,
    averagePerformance: averagePerformance.content,
    currentLearningYear,
    markType,
  });
  const isLoading = finalPerformance.loading || averagePerformance.loading;

  const hasAnySubjects = currentPerformance.subjectsPerformance && currentPerformance.subjectsPerformance.length > 0;
  const isOptionsVisible = !isLoading;

  // общая заглушка - нет объектов в массиве subjectsPerformance
  const hasNoDataPlug = !hasAnySubjects;

  const showYearSelection =
    currentVisibilityType === VisibilityType.TILES || currentVisibilityType === VisibilityType.DIAGRAM;

  useEffect(() => {
    // Сбрасывает выбранный год в фильтрах на год по умолчанию при MarkType = Average,
    //  т.к. изменение фильтра год для средней оценки недоступно
    if (markType === MarkType.AVERAGE && filterYear !== initialFilterYear) {
      setFilterYear(initialFilterYear);
    }
  }, [markType]);

  useEffect(() => {
    if (currentVisibilityType === VisibilityType.CHART && markType === MarkType.AVERAGE) {
      setMarkType(MarkType.FINAL);
    }
  }, [currentVisibilityType]);

  // Обертка над setMarkType, для проверки наличия в новых данных текущего года
  // При отсутствии года в новых данных будет установлен первый год из списка
  const handleChangeMarkType = (type: MarkType) => {
    emitYMEvent({ type: 'averageTotalStudySwitching' });

    setMarkType(type);

    // const nextPerformanceData = type === MarkType.AVERAGE ? averagePerformance : finalPerformance;
    // const learningYears = nextPerformanceData.content.map((performance) => performance.year);
    // const sortedLearningYears = sortLearningYears(learningYears, 'desc');

    // if (!currentLearningYear || !sortedLearningYears.includes(currentLearningYear)) {
    //   setFilterYear(initialFilterYear);
    // }
  };

  const handleVisibilityTypeChange = (type: VisibilityType) => {
    const typeToNameMapping: Record<VisibilityType, 'radarChart' | 'gridTiles' | 'lineChart'> = {
      [VisibilityType.DIAGRAM]: 'radarChart',
      [VisibilityType.TILES]: 'gridTiles',
      [VisibilityType.CHART]: 'lineChart',
    };
    emitYMEvent({
      type: 'graphViewSwitching',
      payload: {
        typeChart: typeToNameMapping[type],
      },
    });

    setCurrentVisibilityType(type);

    // Нужно рассчитывать новый индекс для выбранного фильтра,
    // т.к. его может не быть, либо он может быть с новым порядковым номером
    if (markType === MarkType.FINAL) {
      const currentOption = learningYearOptions.find((option) => option.code === filterYear)?.value;

      if (!currentOption) {
        return;
      }

      const currentOptionIndex = learningYearOptions.findIndex((option) => option.code === filterYear);

      const years = type === VisibilityType.DIAGRAM ? finalMarkDiagramLearningYears : finalMarkUnfilteredYears;
      const newSortedDiagramYears = sortLearningYears(years, 'desc');
      const newChosenYearIndex = newSortedDiagramYears.findIndex((year) => year === currentOption);

      if (newChosenYearIndex === -1) {
        setFilterYear(initialFilterYear);
      } else if (newChosenYearIndex !== currentOptionIndex) {
        setFilterYear(newChosenYearIndex + 1);
      }
    }
  };

  const visibilityTypeSwitcherElement = (
    <Switcher
      value={currentVisibilityType}
      onChange={handleVisibilityTypeChange}
      componentSize={isMobile ? 'small' : 'medium'}
      stretchy={isMobile}
    >
      <SwitcherButton value={VisibilityType.DIAGRAM} content={<IconPieChartFilled />} />
      <SwitcherButton value={VisibilityType.TILES} content={<IconGridFilled />} />
      <SwitcherButton value={VisibilityType.CHART} content={<IconBarChart />} />
    </Switcher>
  );

  const handleChangeFilterYear = (year: number) => {
    emitYMEvent({ type: 'yearStudySwitching' });
    setFilterYear(year);
  };

  const filtersElement =
    currentVisibilityType === VisibilityType.CHART ? null : (
      <PerformanceFilters
        markType={markType}
        onChangeMarkType={handleChangeMarkType}
        year={filterYear}
        onChangeYear={handleChangeFilterYear}
        yearOptions={mappedLearningYearOptions}
        withYearSelector={showYearSelection}
        isMobile={isMobile}
        isDisabled={false}
      />
    );

  const generalNoDataPlug = (
    <NoDataPlug
      image={placeholderCharacter}
      title="Данных по предметным результатам пока нет"
      subtitle="Как только они появятся, информация о них отобразится здесь"
      loading={isLoading}
    />
  );

  if (!isWidgetVisible) return null;

  return (
    <Widget containerData={studyNavigation.performance} withBackground={isBrightTheme}>
      {overlayElement}
      <Widget.Header
        title={<WidgetContainerTitle title="Предметные результаты" right={isSettingsMode && toggleElement} />}
        options={
          isOptionsVisible && <WidgetContainerOptions left={visibilityTypeSwitcherElement} right={filtersElement} />
        }
        backgroundImage={widgetIllustrations.studyPerformance}
      />
      <Widget.Padding borderTop="1px solid #e8e8ef">
        {hasNoDataPlug && generalNoDataPlug}

        {!hasNoDataPlug && (
          <PerformanceContainer
            currentPerformance={currentPerformance}
            currentVisibilityType={currentVisibilityType}
            markType={markType}
          />
        )}
      </Widget.Padding>
    </Widget>
  );
};

export default PerformanceWidgetContainer;
